import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useTranslation, parseApiError, useSettings } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { Plus, Edit, Trash2, CheckSquare, X, Loader2 } from 'lucide-react';
import Pagination from '../components/Pagination';

const ApprovalConfigPage = () => {
  const t = useTranslation();
  const { settings } = useSettings();
  
  const [configs, setConfigs] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({ name: '', steps: [] });
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchData();
  }, [page]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configsRes, rolesRes, usersRes] = await Promise.all([
        axios.get(`${API}/approval-configs`, { params: { page, page_size: 10 } }),
        axios.get(`${API}/roles`, { params: { page_size: 100 } }),
        axios.get(`${API}/members`, { params: { page_size: 100 } })
      ]);
      setConfigs(configsRes.data.items);
      setTotalPages(configsRes.data.total_pages);
      setRoles(rolesRes.data.items);
      setUsers(usersRes.data.items);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingId(null);
    setFormData({ 
      name: '', 
      steps: [{ order: 1, approver_role_id: '', approver_user_id: null }] 
    });
    setDialogOpen(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setFormData({ name: item.name, steps: item.steps || [] });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Name is required');
      return;
    }
    if (formData.steps.length === 0) {
      toast.error('At least one approval step is required');
      return;
    }

    // Validate steps
    for (const step of formData.steps) {
      if (!step.approver_role_id && !step.approver_user_id) {
        toast.error('Each step must have either a role or user assigned');
        return;
      }
    }

    setSaving(true);
    try {
      if (editingId) {
        await axios.put(`${API}/approval-configs/${editingId}`, formData);
        toast.success('Approval config updated');
      } else {
        await axios.post(`${API}/approval-configs`, formData);
        toast.success('Approval config created');
      }
      setDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/approval-configs/${deleteId}`);
      toast.success('Approval config deleted');
      fetchData();
    } catch (error) {
      toast.error(parseApiError(error) || 'Failed to delete');
    } finally {
      setDeleteId(null);
    }
  };

  const addStep = () => {
    setFormData(prev => ({
      ...prev,
      steps: [...prev.steps, { order: prev.steps.length + 1, approver_role_id: '', approver_user_id: null }]
    }));
  };

  const updateStep = (index, key, value) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.map((s, i) => i === index ? { ...s, [key]: value } : s)
    }));
  };

  const removeStep = (index) => {
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index).map((s, i) => ({ ...s, order: i + 1 }))
    }));
  };

  const getRoleName = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    return role?.name || '-';
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId);
    return user?.name || '-';
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="approval-config-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 font-['Outfit'] tracking-tight">
            {t('approvalConfig')}
          </h1>
          <p className="text-gray-500 mt-1">Configure document approval workflows</p>
        </div>
        <Button 
          onClick={handleCreate}
          className="gap-2"
          data-testid="create-approval-config-btn"
        >
          <Plus size={18} />
          {t('addNew')}
        </Button>
      </div>

      {/* Table */}
      <Card className="border-gray-200">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : configs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <CheckSquare className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">{t('noResults')}</p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead className="font-semibold text-gray-600">{t('name')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('approvalSteps')}</TableHead>
                  <TableHead className="font-semibold text-gray-600">{t('createdAt')}</TableHead>
                  <TableHead className="text-right font-semibold text-gray-600">{t('actions')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {configs.map((item) => (
                  <TableRow key={item.id} className="hover:bg-gray-50">
                    <TableCell className="font-medium">{item.name}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {item.steps?.map((step, idx) => (
                          <React.Fragment key={idx}>
                            <span className="px-2 py-1 bg-gray-100 rounded text-xs">
                              {step.approver_user_id 
                                ? getUserName(step.approver_user_id) 
                                : getRoleName(step.approver_role_id)
                              }
                            </span>
                            {idx < item.steps.length - 1 && (
                              <span className="text-gray-400">→</span>
                            )}
                          </React.Fragment>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>{new Date(item.created_at).toLocaleDateString()}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEdit(item)}
                          data-testid={`edit-approval-config-${item.id}`}
                        >
                          <Edit size={16} />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => setDeleteId(item.id)}
                          data-testid={`delete-approval-config-${item.id}`}
                        >
                          <Trash2 size={16} />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Outfit']">
              {editingId ? 'Edit Approval Config' : 'Create Approval Config'}
            </DialogTitle>
            <DialogDescription>
              Define the approval workflow steps
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t('name')}</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Two-Level Approval"
                data-testid="approval-config-name-input"
              />
            </div>

            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label>{t('approvalSteps')}</Label>
                <Button type="button" size="sm" variant="outline" onClick={addStep} data-testid="add-step-btn">
                  <Plus size={14} className="mr-1" />
                  Add Step
                </Button>
              </div>

              <div className="space-y-3">
                {formData.steps.map((step, idx) => (
                  <div 
                    key={idx} 
                    className="p-4 border border-gray-200 rounded-lg bg-gray-50"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-sm font-medium text-gray-600">
                        {t('step')} {step.order}
                      </span>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-gray-400 hover:text-red-600 h-6 w-6"
                        onClick={() => removeStep(idx)}
                        data-testid={`remove-step-${idx}`}
                      >
                        <X size={14} />
                      </Button>
                    </div>

                    <div className="space-y-3">
                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Approver Role</Label>
                        <Select 
                          value={step.approver_role_id || 'none'} 
                          onValueChange={(v) => updateStep(idx, 'approver_role_id', v === 'none' ? '' : v)}
                        >
                          <SelectTrigger data-testid={`step-role-${idx}`}>
                            <SelectValue placeholder="Select role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none" disabled>Select a role</SelectItem>
                            {roles.map(role => (
                              <SelectItem key={role.id} value={role.id}>
                                {role.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>

                      <div className="space-y-1">
                        <Label className="text-xs text-gray-500">Specific User (Optional)</Label>
                        <Select 
                          value={step.approver_user_id || 'none'} 
                          onValueChange={(v) => updateStep(idx, 'approver_user_id', v === 'none' ? null : v)}
                        >
                          <SelectTrigger data-testid={`step-user-${idx}`}>
                            <SelectValue placeholder="Any user with role" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Any user with role</SelectItem>
                            {users.map(user => (
                              <SelectItem key={user.id} value={user.id}>
                                {user.name} ({user.email})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>
                ))}

                {formData.steps.length === 0 && (
                  <div className="text-center py-8 border border-dashed border-gray-300 rounded-lg">
                    <p className="text-gray-500">No steps added yet</p>
                    <Button 
                      type="button" 
                      variant="outline" 
                      size="sm" 
                      className="mt-2"
                      onClick={addStep}
                    >
                      <Plus size={14} className="mr-1" />
                      Add first step
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              {t('cancel')}
            </Button>
            <Button 
              onClick={handleSave} 
              disabled={saving}
              data-testid="save-approval-config-btn"
            >
              {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
              {t('save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this approval configuration.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ApprovalConfigPage;
